import { getCanonicalMarketingRoute, getMarketingPageTranslations } from "../../marketing-content/marketingPageTranslations";
import { MARKETING_LOCALES, MarketingLanguage } from "../../marketing-content/marketingLocales";
import React, { useCallback, useMemo } from "react";
import { trackMarketingSitePageView } from "../../utils/analytics/marketing-site-screen-view";
import { useLandingTheme } from "../landing-page/LandingThemeContext";
import { reportContinueInBrowserChosenBeforeHydration } from "../landing-page/handoff/appStoreRoute";
import { isMarketingBlogPost, MarketingContentIndexEntry } from "../../marketing-content/marketingContentLoader";
import { buildBlogPostingJsonLd } from "../../marketing-content/marketingStructuredData";
import { MarketingDocumentHead } from "./MarketingDocumentHead";

// Canonical URLs always point at the marketing domain, whichever origin serves the page.
export const MARKETING_SITE_ORIGIN = "https://jungleai.com";
// The link preview image every marketing page shares.
const DEFAULT_SOCIAL_IMAGE_PATH = "/images/marketing/social-link-preview.png";

/**
 * The SEO head tags for one marketing page. The build-time static HTML
 * (scripts/generate-marketing-static-html.mjs) emits the same tags with the same values.
 * MarketingDocumentHead supplies document attributes and fonts in the same update.
 */
export const MarketingSeoHead = React.memo(function MarketingSeoHead({ entry }: { entry: MarketingContentIndexEntry }) {
  const canonicalUrl = `${MARKETING_SITE_ORIGIN}${getCanonicalMarketingRoute(entry.route)}`;
  const translations = getMarketingPageTranslations(entry.route);
  const englishPageUrl = `${MARKETING_SITE_ORIGIN}${translations.en ?? entry.route}`;
  const socialImageUrl = `${MARKETING_SITE_ORIGIN}${DEFAULT_SOCIAL_IMAGE_PATH}`;
  // A blog post's own hero image is still named in its structured data.
  const blogPostImageUrl = `${MARKETING_SITE_ORIGIN}${entry.heroImage ?? DEFAULT_SOCIAL_IMAGE_PATH}`;
  const isBlogPost = isMarketingBlogPost(entry);
  const blogPostingJsonLd = useMemo(
    () => (isBlogPost ? buildBlogPostingJsonLd({ entry, canonicalUrl, imageUrl: blogPostImageUrl, htmlLanguage: MARKETING_LOCALES[entry.lang].htmlLang }) : null),
    [entry, isBlogPost, canonicalUrl, blogPostImageUrl]
  );

  const theme = useLandingTheme();
  // Runs after react-helmet has updated the head, so the page view is recorded with this page's title.
  const reportPageView = useCallback(() => {
    trackMarketingSitePageView({
      pageViewEventName: "Marketing Page Viewed",
      pageViewEventProperties: { route: entry.route, section: entry.section, template: entry.template, lang: entry.lang, landingVariant: theme.id },
    });
    // The landing template's hero has "Continue in browser": a tap before hydration is reported after the view.
    reportContinueInBrowserChosenBeforeHydration();
  }, [entry.route, entry.section, entry.template, entry.lang, theme.id]);

  return (
    <MarketingDocumentHead language={entry.lang} onChangeClientState={reportPageView}>
      <title>{entry.title}</title>
      <meta name="description" content={entry.description} />
      <link rel="canonical" href={canonicalUrl} />
      {Object.entries(translations).map(([language, route]) => <link key={language} rel="alternate" hrefLang={MARKETING_LOCALES[language as MarketingLanguage].hreflang} href={`${MARKETING_SITE_ORIGIN}${route}`} />)}
      <link rel="alternate" hrefLang="x-default" href={englishPageUrl} />
      <meta property="og:type" content={isBlogPost ? "article" : "website"} />
      <meta property="og:title" content={entry.title} />
      <meta property="og:description" content={entry.description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={socialImageUrl} />
      <meta name="twitter:card" content="summary_large_image" />
      {blogPostingJsonLd && <script type="application/ld+json">{blogPostingJsonLd}</script>}
    </MarketingDocumentHead>
  );
});
